/**
 * C2 — "Portfolios you've left".
 *
 * The one fact this card exists to state: leaving revokes nothing. The
 * organisation's structural view of you stops on its own, and every project
 * grant you made stays until YOU revoke it. That is deliberate — those grants
 * were made one at a time and are yours to take back one at a time — but it is
 * also surprising, so the screen says it in the card and puts the button that
 * changes it right there.
 */
import { useState } from 'react'
import { shortDate } from './sharingModel'
import type { FormerPortfolio } from './sharingModel'

export interface PortfoliosYouveLeftProps {
  portfolios: FormerPortfolio[]
  loaded: boolean
  /** Revokes every remaining grant this organisation holds. */
  onRevokeAll: (aggregatorId: string) => Promise<void>
}

function Row({ former, onRevokeAll }: {
  former: FormerPortfolio
  onRevokeAll: (id: string) => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const when = shortDate(former.endedAt)

  const revoke = async () => {
    setBusy(true)
    setErrorMessage(null)
    try {
      await onRevokeAll(former.aggregatorId)
      setConfirming(false)
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Could not revoke that view.'
      setErrorMessage(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border-t border-slate-100 py-2 first:border-t-0">
      <div className="flex items-center gap-3 text-[12.5px] text-slate-600">
        <span className="min-w-0 flex-1">
          <b className="text-slate-900">{former.organisation}</b>{' '}
          ({former.endedBy === 'you' ? 'you left' : 'ended'}{when ? ` ${when}` : ''})
          {former.stillHolds > 0
            ? ` · still holds view on ${former.stillHolds} project${former.stillHolds === 1 ? '' : 's'}`
            : ' · holds no view on any project'}
        </span>
        {former.stillHolds > 0 && (
          <button
            type="button"
            onClick={() => (confirming ? revoke() : setConfirming(true))}
            disabled={busy}
            className="shrink-0 rounded border border-rose-200 px-2.5 py-1 text-[11.5px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          >
            {busy ? 'Revoking…' : confirming ? 'Confirm revoke' : 'Revoke'}
          </button>
        )}
      </div>
      {confirming && !busy && (
        <p className="mt-1 text-[11.5px] text-slate-500">
          They lose access to every document in{' '}
          {former.stillHolds === 1 ? 'that project' : 'those projects'} immediately.
          Anything they already read is not recalled.
        </p>
      )}
      {errorMessage && <p className="mt-1 text-[11.5px] text-rose-600">{errorMessage}</p>}
    </div>
  )
}

export default function PortfoliosYouveLeft({ portfolios, loaded, onRevokeAll }: PortfoliosYouveLeftProps) {
  return (
    <div data-part="PortfoliosYouveLeft" className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <h2 className="mb-1 flex items-center gap-2 text-[14px] font-bold text-slate-900">
        Portfolios you&apos;ve left
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">
          {loaded ? portfolios.length : '—'}
        </span>
      </h2>

      {!loaded && <p className="text-[12.5px] text-slate-400">Loading…</p>}
      {loaded && portfolios.length === 0 && (
        <p className="text-[12.5px] text-slate-600">
          You haven&apos;t left any portfolio.
        </p>
      )}
      {loaded && portfolios.map((f) => (
        <Row key={f.aggregatorId} former={f} onRevokeAll={onRevokeAll} />
      ))}

      <p className="mt-2 text-[11.5px] leading-relaxed text-slate-500">
        When you leave a portfolio, the grants you made stay until you revoke
        them — their structural view stops on its own.
      </p>
    </div>
  )
}
